import { apiClient } from "./apiClient";

interface OdooMigrationConfig {
  parallelWorkers?: number;
  batchSize?: number;
  useCompression?: boolean;
  continueOnError?: boolean;
  timeoutSeconds?: number;
  targetDatabase?: string;
  optimizeMemory?: boolean;
}

interface MigrationResponse {
  success: boolean;
  message: string;
  data: {
    duration: number;
    fileSize: string;
    recordsProcessed: number;
    sqlFile: string;
    optimizations: any;
  };
}

interface MigrationStatus {
  isRunning: boolean;
  progress: number;
  currentStep: string;
  estimatedTimeRemaining: number;
}

/**
 * Servicio para operaciones de Odoo Remote Sync
 * Optimizado para AWS EC2 m8g.xlarge
 * Usa apiClient centralizado con interceptores automáticos
 */
class OdooService {
  /**
   * Ejecuta migración de base de datos Odoo desde archivo local en el servidor
   * Optimizado para evitar problemas de upload de archivos grandes
   */
  async executeDatabaseMigration(
    config: OdooMigrationConfig = {},
  ): Promise<MigrationResponse> {
    try {
      const migrationData = {
        parallelWorkers: config.parallelWorkers || 8,
        batchSize: config.batchSize || 10000,
        timeoutSeconds: config.timeoutSeconds || 1800, // 30 minutos por defecto
        useCompression: config.useCompression ?? true,
        continueOnError: config.continueOnError ?? false,
        optimizeMemory: config.optimizeMemory ?? true,
        targetDatabase: config.targetDatabase || "odoo_16",
      };

      const response = await apiClient.post<MigrationResponse>(
        "/odoo/dump-database",
        migrationData,
        {
          timeout: (config.timeoutSeconds || 1800) * 1000, // Default 30 minutos
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error ejecutando migración:", error);
      throw error;
    }
  }

  /**
   * Obtiene el estado actual de la migración
   */
  async getMigrationStatus(): Promise<MigrationStatus> {
    try {
      const response = await apiClient.get<MigrationStatus>(
        "/odoo/migration-status",
      );
      return response.data;
    } catch (error) {
      console.error("Error obteniendo estado de migración:", error);
      throw error;
    }
  }

  /**
   * Cancela migración en progreso
   */
  async cancelMigration(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{
        success: boolean;
        message: string;
      }>("/odoo/cancel-migration");
      return response.data;
    } catch (error) {
      console.error("Error cancelando migración:", error);
      throw error;
    }
  }

  /**
   * Detecta el entorno actual (desarrollo vs producción)
   */
  private detectEnvironment(): "development" | "production" {
    // En desarrollo: localhost o 127.0.0.1
    const isDevelopment =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "0.0.0.0" ||
      window.location.port === "5173"; // Puerto de Vite dev server

    return isDevelopment ? "development" : "production";
  }

  /**
   * Obtiene configuración optimizada basada en el entorno detectado
   */
  getOptimizedConfig(
    forceEnvironment?: "development" | "production",
  ): OdooMigrationConfig {
    const environment = forceEnvironment || this.detectEnvironment();

    const configs = {
      development: {
        // Configuración para MacBook Pro M4 (desarrollo local) - COMPATIBLE CON DTO BACKEND
        parallelWorkers: 4, // M4 tiene 10 cores, usar 4 (min: 1, max: 16) ✅
        batchSize: 2000, // Lotes pequeños para desarrollo (min: 1000, max: 50000) ✅
        useCompression: false, // Más rápido sin compresión en desarrollo ✅
        continueOnError: true, // Continuar en errores para testing ✅
        timeoutSeconds: 600, // 10 minutos max en desarrollo (min: 300, max: 10800) ✅
        optimizeMemory: false, // MacBook maneja bien la memoria ✅
        targetDatabase: "odoo_16", // Base de datos de desarrollo ✅
      },
      production: {
        // Configuración para AWS EC2 m8g.xlarge (producción remota) - COMPATIBLE CON DTO BACKEND
        parallelWorkers: 8, // ARM64 con 4 vCPUs, usar 8 workers (min: 1, max: 16) ✅
        batchSize: 10000, // Lotes grandes para eficiencia (min: 1000, max: 50000) ✅
        useCompression: true, // Optimizar ancho de banda ✅
        continueOnError: false, // Fallar rápido en producción ✅
        timeoutSeconds: 1800, // 30 minutos max en producción (min: 300, max: 10800) ✅
        optimizeMemory: true, // 32GB RAM, optimizar uso ✅
        targetDatabase: "odoo_16", // Base de datos de producción AWS remota ✅
      },
    };
    return configs[environment];
  }

  /**
   * Obtiene información del entorno actual
   */
  getEnvironmentInfo() {
    const environment = this.detectEnvironment();
    const config = this.getOptimizedConfig();

    const environmentInfo = {
      development: {
        name: "Desarrollo Local",
        platform: "MacBook Pro M4",
        description: "Configuración optimizada para desarrollo local",
        database: "odoo_16",
        icon: "💻",
      },
      production: {
        name: "Producción AWS",
        platform: "EC2 m8g.xlarge (ARM64) - Remoto",
        description: "Configuración optimizada para AWS EC2 remoto",
        database: "odoo_16",
        icon: "☁️",
      },
    };

    return {
      environment,
      config,
      info: environmentInfo[environment],
    };
  }

  /**
   * Valida archivo SQL antes de subir
   */
  validateSqlFile(file: File): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validar que el archivo existe
    if (!file) {
      errors.push("No se ha seleccionado ningún archivo");
      return { isValid: false, errors };
    }

    // Validar extensión
    const validExtensions = [".sql", ".dump", ".bak"];
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."));
    if (!validExtensions.includes(fileExtension)) {
      errors.push(
        `Extensión '${fileExtension}' no válida. Extensiones permitidas: ${validExtensions.join(", ")}`,
      );
    }

    // Validar tamaño (máximo 10GB para m8g.xlarge como se menciona en UI)
    const maxSize = 10 * 1024 * 1024 * 1024; // 10GB
    if (file.size > maxSize) {
      errors.push(
        `Archivo de ${this.formatFileSize(file.size)} excede el límite máximo de ${this.formatFileSize(maxSize)}`,
      );
    }

    // Validar tamaño mínimo
    const minSize = 1024; // 1KB
    if (file.size < minSize) {
      errors.push(
        `Archivo de ${this.formatFileSize(file.size)} es demasiado pequeño. Tamaño mínimo: ${this.formatFileSize(minSize)}`,
      );
    }

    // Validar nombre del archivo
    if (file.name.length < 4) {
      errors.push("El nombre del archivo es demasiado corto");
    }

    // Validar caracteres especiales en nombre
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(file.name)) {
      errors.push(
        'El nombre del archivo contiene caracteres no válidos: < > : " / \\ | ? *',
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Formatea bytes a formato legible
   */
  formatFileSize(bytes: number): string {
    const units = ["B", "KB", "MB", "GB", "TB"];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(2)} ${units[unitIndex]}`;
  }

  /**
   * Formatea duración en segundos a formato legible
   */
  formatDuration(seconds: number): string {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }

  /**
   * Estado de conexión con el backend
   */
  async checkBackendConnection(): Promise<boolean> {
    try {
      await apiClient.get("/");
      return true;
    } catch (error) {
      console.error("Backend no disponible:", error);
      return false;
    }
  }
}

// Instancia singleton del servicio
export const odooService = new OdooService();

// Exportar tipos para uso en otros archivos
export type { OdooMigrationConfig, MigrationResponse, MigrationStatus };
